import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getWorkspaceSelectionKey, sanitizeWorkspaceSelection } from "../workspaceSelection/workspaceSelectionUtils";

const STORAGE_KEY = "lead3a.admin.sessionSipActivity.v1";
const SessionSipActivityContext = createContext(null);

function readStoredActivity() {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "[]");
    if (!Array.isArray(value)) return [];
    const unique = new Map();
    value.forEach((item) => {
      const key = getWorkspaceSelectionKey({ ...item, entityType: "sipClient" });
      if (!key) return;
      const sanitizedSelection = sanitizeWorkspaceSelection([{ ...item, entityType: "sipClient" }])[0];
      if (!sanitizedSelection) return;
      unique.set(key, {
        ...sanitizedSelection,
        key,
        changedFields: Array.isArray(item.changedFields) ? item.changedFields.filter((field) => typeof field === "string").slice(0, 20) : [],
        changedAt: typeof item.changedAt === "string" ? item.changedAt : new Date().toISOString(),
        source: typeof item.source === "string" ? item.source.slice(0, 80) : "unknown",
      });
    });
    return Array.from(unique.values());
  } catch { return []; }
}

export function SessionSipActivityProvider({ children }) {
  const [items, setItems] = useState(readStoredActivity);

  useEffect(() => {
    try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
  }, [items]);

  const recordChanges = useCallback((changes, source) => {
    setItems((current) => {
      const byKey = new Map(current.map((item) => [item.key, item]));
      changes.forEach((change) => {
        const selectionItem = { ...change, entityType: "sipClient" };
        const key = getWorkspaceSelectionKey(selectionItem);
        const fields = [...new Set((change.changedFields || []).filter(Boolean))];
        if (!key || fields.length === 0) return;
        const previous = byKey.get(key);
        byKey.set(key, {
          entityType: "sipClient",
          key,
          complexId: String(change.complexId),
          sipClientId: String(change.sipClientId),
          clientName: change.clientName,
          snapshot: { ...(previous?.snapshot || {}), ...(change.snapshot || {}) },
          changedFields: [...new Set([...(previous?.changedFields || []), ...fields])],
          changedAt: new Date().toISOString(),
          source,
        });
      });
      return Array.from(byKey.values());
    });
  }, []);

  const clearActivity = useCallback(() => setItems([]), []);
  const activityByKey = useMemo(() => new Map(items.map((item) => [item.key, item])), [items]);
  const keys = useMemo(() => new Set(activityByKey.keys()), [activityByKey]);
  const isChanged = useCallback((item) => keys.has(getWorkspaceSelectionKey(item)), [keys]);
  const getActivity = useCallback((item) => {
    const key = getWorkspaceSelectionKey(item);
    return activityByKey.get(key) || null;
  }, [activityByKey]);
  const selectionItems = useMemo(() => sanitizeWorkspaceSelection(items), [items]);
  const value = useMemo(() => ({ items, selectionItems, recordChanges, clearActivity, isChanged, getActivity }), [clearActivity, getActivity, isChanged, items, recordChanges, selectionItems]);

  return <SessionSipActivityContext.Provider value={value}>{children}</SessionSipActivityContext.Provider>;
}

export function useSessionSipActivity() {
  const value = useContext(SessionSipActivityContext);
  if (!value) throw new Error("useSessionSipActivity must be used inside SessionSipActivityProvider");
  return value;
}
